/**
 * 74.22 stage 1 (DECISIONS 443 (1), (2); ARCHITECTURE §5.53 §2 lever (a)): the generator's roughness as band-limited
 * lattice noise instead of a hashed offset at every vertex.
 *
 * The per-vertex hash (`mapgen.ts positionJitter × TERRAIN_PROFILES.jitter`) is white noise at the mesh's own
 * frequency — the sharpest thing a mesh can hold: on the rough worlds it creased 28–61 % of the edges past 10°, and one
 * subdivision finer doubled the creases (the sharpness sweep, sweeps/2026-11-03-74.22-sharpness.md). This is the same
 * roughness as a field on the sphere with a wavelength of a few facets **of the planet's generated detail, in radians**
 * (`GROUND_JITTER.facets` × the facet's angle at `planetDetailForRadius`), so the finer mesh samples the same world and
 * a facet is never creased by its own noise: two octaves of trilinear value noise on an integer lattice (the fine
 * terrain's kind, core/fineTerrain.ts: Math.imul hashing and a smoothstep blend — + − × and floor only, so every
 * engine agrees to the bit), the lattice translated by the planet's first octave offsets so worlds differ at no rng
 * draw. A function of the unit direction alone, so the non-indexed icosphere's coincident corners agree (no seams).
 *
 * Behind `MatchSetup.groundJitter` on the action clock (the Lab's Jitter row, `?jitter=lattice`); absent, the per-vertex
 * hash as before in every byte — the classic game never takes it.
 */

/** The generator's jitter: `vertex` the hashed offset at every mesh vertex (today's), `lattice` the band-limited field. */
export type GroundJitter = 'vertex' | 'lattice';

export const GROUND_JITTER = {
  /** The field's base wavelength in facets of the planet's generated mesh (the sweep: the smooth field under the jitter is 7–14 facets a wavelength; the jitter's roughness sits at 3–4). */
  facets: 3.5,
  /** The second octave: half the wavelength at this share of the first. */
  octave2: 0.5,
  /** The field's gain, so its rms over the sphere matches the hash's (a uniform in [−1, 1): rms 0.578; the two blended octaves' 0.41 at gain 1 — 200,000 points on the sphere, the same at detail 4 and 5), so the profiles' `jitter` amplitudes keep their meaning. */
  gain: 1.42,
} as const;

/** The angle an icosphere's edge subtends at `detail` subdivisions, radians (the icosahedron's 1.1071 rad halved each level; the mesh's edges vary ±15 % about it). */
export function facetAngleOf(detail: number): number {
  return 1.1071 / (1 << detail);
}

const corner = new Float64Array(8);

/** One octave of value noise at the lattice coordinates (x, y, z): in [0, 1). The fine terrain's octave, with the key mixed into each corner's hash. */
function octave(x: number, y: number, z: number, key: number): number {
  const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
  const fx = x - x0, fy = y - y0, fz = z - z0;
  const tx = fx * fx * (3 - 2 * fx), ty = fy * fy * (3 - 2 * fy), tz = fz * fz * (3 - 2 * fz);
  for (let c = 0; c < 8; c++) {
    let h = Math.imul(x0 + (c & 1), 0x8da6b343) ^ Math.imul(y0 + ((c >> 1) & 1), 0xd8163841) ^ Math.imul(z0 + (c >> 2), 0xcb1ab31f) ^ key;
    h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
    h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
    h ^= h >>> 15;
    corner[c] = (h >>> 0) / 4294967296;
  }
  const x00 = corner[0] + (corner[1] - corner[0]) * tx;
  const x10 = corner[2] + (corner[3] - corner[2]) * tx;
  const x01 = corner[4] + (corner[5] - corner[4]) * tx;
  const x11 = corner[6] + (corner[7] - corner[6]) * tx;
  const y0v = x00 + (x10 - x00) * ty;
  const y1v = x01 + (x11 - x01) * ty;
  return y0v + (y1v - y0v) * tz;
}

/**
 * The lattice jitter at the unit direction (nx, ny, nz), in about [−1, 1) (the gain can push a rare point past it, as
 * the clamp above takes care of): two octaves at `wavelength` radians and half of it, the lattice translated by
 * (ox, oy, oz) — the planet's first octave offsets, so each world's field is its own.
 */
export function latticeJitter(nx: number, ny: number, nz: number, wavelength: number, ox: number, oy: number, oz: number): number {
  const f1 = 1 / wavelength, f2 = 2 * f1;
  const a = octave(nx * f1 + ox, ny * f1 + oy, nz * f1 + oz, 0x74220001) * 2 - 1;
  const b = octave(nx * f2 + oy, ny * f2 + oz, nz * f2 + ox, 0x74220002) * 2 - 1;
  return (a + GROUND_JITTER.octave2 * b) * GROUND_JITTER.gain;
}
